import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyOrdersApi } from '../api/orders';
import LogoBlack from '../assets/img/MOSIDA-black.png';
import QuitLeaveModal from '../components/modals/QuitLeaveModal';
import BackArrowIcon from '../components/icons/BackArrowIcon';
import { toCdnMediaUrl } from '../utils/cdnMediaUrl';
import { readAuthSession } from '../services/authSession';
import { clearKioskSession } from '../utils/kioskSession';
import {
  getStoredTicket,
  ticketUserId,
} from '../utils/ticket';

const ORDERS_PAGE_SIZE = 7;

function resolveCurrentUserId() {
  const ticket = getStoredTicket();
  const auth = readAuthSession();
  return (
    ticketUserId(ticket) ||
    (auth?.user?.id ? String(auth.user.id) : '') ||
    ''
  );
}

function OrdersFooter({ onCheckout }) {
  return (
    <div className="ordersFooter">
      <p className="ordersFooter__copy">Want to checkout more of your art?</p>
      <button type="button" className="ordersFooter__cta" onClick={onCheckout}>
        CHECKOUT FROM MY GALLERY <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}

function MyOrder() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quitOpen, setQuitOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(rows.length / ORDERS_PAGE_SIZE));

  const pageRows = useMemo(() => {
    const start = (page - 1) * ORDERS_PAGE_SIZE;
    return rows.slice(start, start + ORDERS_PAGE_SIZE);
  }, [rows, page]);

  const loadOrders = useCallback(async () => {
    const userId = resolveCurrentUserId();
    const auth = readAuthSession();
    if (!userId && !auth?.token) {
      setRows([]);
      setPage(1);
      setError('Sign in or scan a ticket to see your orders.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { rows: nextRows } = await getMyOrdersApi({ userId });
      setRows(nextRows);
      setPage(1);
      if (!nextRows.length) {
        setError('No orders found for this user yet.');
      }
    } catch (err) {
      setRows([]);
      setPage(1);
      setError(err?.message || 'Could not load your orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const confirmQuit = () => {
    clearKioskSession();
    setRows([]);
    setPage(1);
    setError('');
    setQuitOpen(false);
    navigate('/', { replace: true, state: { sessionResetAt: Date.now() } });
  };

  const goGallery = () => navigate('/my-gallery');

  return (
    <div className="galleryPage ordersPage">
      <header className="galleryPage__header">
        <button type="button" className="galleryPage__back" onClick={goGallery}>
          <BackArrowIcon />
          Back
        </button>
        <img className="galleryPage__logo" src={LogoBlack} alt="MOSIDA" />
        <button type="button" className="galleryPage__quit" onClick={() => setQuitOpen(true)}>
          Quit
        </button>
      </header>

      <h1 className="galleryPage__title">YOUR ORDER STATUS</h1>

      <nav className="galleryPage__tabs" aria-label="Gallery sections">
        <button type="button" className="galleryPage__tab" onClick={goGallery}>
          MY GALLERY
        </button>
        <button type="button" className="galleryPage__tab is-active" aria-current="page">
          MY ORDERS
        </button>
      </nav>

      <div className="ordersBody">
        <div className="ordersTableCard">
          {loading ? (
            <>
              <div className="galleryPage__loader" role="status" aria-live="polite">
                <span className="galleryPage__spinner" aria-hidden="true" />
                <p className="galleryPage__status">Loading your orders…</p>
              </div>
              <OrdersFooter onCheckout={goGallery} />
            </>
          ) : rows.length === 0 ? (
            <>
              <div className="galleryPage__empty">
                <p className="galleryPage__status">{error || 'No orders found.'}</p>
                <button type="button" className="galleryPage__retry" onClick={loadOrders}>
                  Try again
                </button>
              </div>
              <OrdersFooter onCheckout={goGallery} />
            </>
          ) : (
            <>
              <div className="ordersTable" role="table" aria-label="Order status">
                <div className="ordersTable__head" role="row">
                  <span role="columnheader">ORDER NO</span>
                  <span role="columnheader">TIME ORDERED</span>
                  <span role="columnheader">ORDER NAME</span>
                  <span role="columnheader">QUANTITY</span>
                  <span role="columnheader">STATUS</span>
                </div>
                <div className="ordersTable__rows">
                  {pageRows.map((row) => {
                    const thumb = row.imageUrl
                      ? toCdnMediaUrl(row.imageUrl) || row.imageUrl
                      : '';
                    return (
                      <div className="ordersTable__row" role="row" key={row.id}>
                        <div className="ordersTable__orderNo" role="cell">
                          {row.orderNo ? `#${row.orderNo}` : '—'}
                        </div>
                        <div className="ordersTable__time" role="cell">
                          <span className="ordersTable__date">{row.dateLine}</span>
                          {row.timeLine ? (
                            <span className="ordersTable__clock">{row.timeLine}</span>
                          ) : null}
                        </div>
                        <div className="ordersTable__name" role="cell">
                          <span className="ordersTable__thumb" aria-hidden={!thumb}>
                            {thumb ? (
                              <img
                                src={thumb}
                                alt=""
                                loading="lazy"
                                decoding="async"
                              />
                            ) : null}
                          </span>
                          <span className="ordersTable__nameText">
                            <strong>{row.name}</strong>
                            {row.subtitle ? (
                              <span className="ordersTable__type">{row.subtitle}</span>
                            ) : null}
                          </span>
                        </div>
                        <div className="ordersTable__qty" role="cell">
                          {row.quantity}
                        </div>
                        <div className="ordersTable__status" role="cell">
                          <span className={`ordersStatus ordersStatus--${row.tone}`}>
                            {row.status}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {totalPages > 1 ? (
                <div className="ordersPagination" aria-label="Orders pagination">
                  <button
                    type="button"
                    className="ordersPagination__btn"
                    aria-label="Previous page"
                    disabled={page <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <span className="ordersPagination__meta">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="ordersPagination__btn"
                    aria-label="Next page"
                    disabled={page >= totalPages}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </div>
              ) : null}

              <OrdersFooter onCheckout={goGallery} />
            </>
          )}
        </div>
      </div>

      <QuitLeaveModal
        show={quitOpen}
        onStay={() => setQuitOpen(false)}
        onLeave={confirmQuit}
      />
    </div>
  );
}

export default MyOrder;
